/**
 * 极练 Rehab & Fit —— 极简专业基础 UI 组件库（阶段 2）。
 * 遵循设计系统规范：全站单品牌色深青绿、16px 卡片圆角、12px 元素圆角、≥48px 可点区域。
 */
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { useEffect } from "react";

export function Card({
  children,
  className = "",
  onClick,
}: {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
}) {
  return (
    <div
      onClick={onClick}
      className={`card-base ${onClick ? "cursor-pointer active:scale-[0.99] transition-transform duration-150" : ""} ${className}`}
    >
      {children}
    </div>
  );
}

type BtnVariant = "primary" | "secondary" | "ghost" | "danger" | "soft";
const BTN_BASE =
  "inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-elem font-semibold text-[15px] transition-all duration-150 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed select-none";

const BTN_VARIANT: Record<BtnVariant, string> = {
  primary: "bg-tone-accent text-white shadow-sm hover:brightness-105 active:opacity-90",
  secondary: "bg-tone-accentSoft text-tone-accent border border-tone-accent/20 active:opacity-80",
  ghost: "bg-transparent text-tone-ink hover:bg-black/5 dark:hover:bg-white/5 active:bg-black/10",
  soft: "bg-black/5 dark:bg-white/10 text-tone-ink border border-tone-border active:opacity-75",
  danger: "bg-tone-danger text-white active:opacity-90",
};

export function Button({
  variant = "primary",
  className = "",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: BtnVariant }) {
  return <button className={`${BTN_BASE} ${BTN_VARIANT[variant]} ${className}`} {...props} />;
}

export function SectionTitle({ children, hint }: { children: ReactNode; hint?: string }) {
  return (
    <div className="flex items-baseline justify-between px-1 mb-2 mt-5">
      <h2 className="text-[15px] font-bold text-tone-ink tracking-tight font-headline">{children}</h2>
      {hint ? <span className="text-[12px] font-medium text-tone-soft font-caption">{hint}</span> : null}
    </div>
  );
}

/** Stat 组件标准形态 = 大数字 + 小单位 + Caption 标签 */
export function Stat({
  label,
  value,
  unit,
  sub,
}: {
  label: string;
  value: ReactNode;
  unit?: string;
  sub?: string;
}) {
  return (
    <div className="card-base p-3.5 flex flex-col justify-between">
      <div className="text-[12px] font-medium text-tone-soft font-caption">{label}</div>
      <div className="flex items-baseline gap-1 mt-1">
        <span className="text-[26px] font-bold text-tone-ink tabular-nums tracking-tight leading-none font-display">
          {value}
        </span>
        {unit ? <span className="text-[12px] font-medium text-tone-soft font-caption">{unit}</span> : null}
      </div>
      {sub ? <div className="text-[11px] font-medium text-tone-muted mt-1.5">{sub}</div> : null}
    </div>
  );
}

export function ProgressBar({
  value,
  tone = "accent",
}: {
  value: number;
  tone?: "accent" | "good" | "warn" | "danger";
}) {
  const pct = Math.max(0, Math.min(100, value));
  const color =
    tone === "good"
      ? "bg-tone-good"
      : tone === "warn"
        ? "bg-tone-warn"
        : tone === "danger"
          ? "bg-tone-danger"
          : "bg-tone-accent";
  return (
    <div className="h-1.5 w-full rounded-full bg-black/5 dark:bg-white/10 overflow-hidden">
      <div className={`h-full rounded-full ${color} transition-all duration-300`} style={{ width: `${pct}%` }} />
    </div>
  );
}

/** 分段选择（药丸胶囊风格，≥48px 热区） */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  tone = "accent",
}: {
  options: { value: T; label: ReactNode; activeColor?: string }[];
  value: T | null;
  onChange: (v: T) => void;
  tone?: "accent" | "good" | "warn" | "danger";
}) {
  const activeCls =
    tone === "good"
      ? "bg-tone-good text-white shadow-sm"
      : tone === "warn"
        ? "bg-tone-warn text-white shadow-sm"
        : tone === "danger"
          ? "bg-tone-danger text-white shadow-sm"
          : "bg-tone-accent text-white shadow-sm";
  return (
    <div className="flex p-1 rounded-elem bg-black/5 dark:bg-white/10 gap-1 min-h-[48px] items-center">
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            onClick={() => onChange(o.value)}
            className={`flex-1 min-h-[40px] px-3 rounded-elem text-[13px] font-semibold transition-all duration-150 active:scale-[0.98] ${
              active ? `${o.activeColor ?? activeCls}` : "text-tone-soft hover:text-tone-ink"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** 数字步进器（加减按钮保证 ≥48px） */
export function Stepper({
  value,
  onChange,
  step = 1,
  min = 0,
  max = 999,
  suffix = "",
}: {
  value: number;
  onChange: (v: number) => void;
  step?: number;
  min?: number;
  max?: number;
  suffix?: string;
}) {
  const clamp = (v: number) => Math.max(min, Math.min(max, Math.round(v * 100) / 100));
  return (
    <div className="flex items-center gap-1.5 p-1 rounded-elem bg-black/5 dark:bg-white/10">
      <button
        aria-label="减"
        className="w-[48px] h-[48px] rounded-elem bg-tone-card text-tone-ink text-xl font-bold active:scale-95 transition-transform flex items-center justify-center shadow-sm"
        onClick={() => onChange(clamp(value - step))}
      >
        −
      </button>
      <div className="flex-1 text-center text-[22px] font-bold text-tone-ink min-w-[64px] tabular-nums font-display">
        {value}
        {suffix ? <span className="text-[12px] text-tone-soft ml-1 font-caption">{suffix}</span> : null}
      </div>
      <button
        aria-label="加"
        className="w-[48px] h-[48px] rounded-elem bg-tone-card text-tone-ink text-xl font-bold active:scale-95 transition-transform flex items-center justify-center shadow-sm"
        onClick={() => onChange(clamp(value + step))}
      >
        +
      </button>
    </div>
  );
}

/** 底部抽屉（高对比度，≥48px 关闭按钮） */
export function Sheet({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex flex-col justify-end bg-black/50 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-tone-card rounded-t-[24px] max-w-[480px] w-full mx-auto pb-safe-bottom max-h-[85vh] flex flex-col shadow-2xl border-t border-tone-border"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-10 h-1.5 bg-black/15 dark:bg-white/20 rounded-full mx-auto mt-3 mb-1" />
        <div className="flex items-center justify-between px-5 pt-2 pb-3 border-b border-tone-border">
          <h3 className="text-[17px] font-bold text-tone-ink font-headline">{title}</h3>
          <button
            onClick={onClose}
            className="w-[48px] h-[48px] rounded-full text-tone-soft hover:text-tone-ink text-sm font-bold flex items-center justify-center active:scale-95"
            aria-label="关闭"
          >
            ✕
          </button>
        </div>
        <div className="overflow-y-auto p-5">{children}</div>
      </div>
    </div>
  );
}

/** Apple 极简开关（≥48px 触控区） */
export function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label?: ReactNode;
}) {
  return (
    <button
      onClick={() => onChange(!checked)}
      aria-pressed={checked}
      className="flex items-center justify-between w-full min-h-[48px] gap-3 select-none"
    >
      {label ? <span className="text-[15px] font-semibold text-tone-ink">{label}</span> : null}
      <span
        className={`relative inline-flex h-7 w-12 rounded-full transition-colors duration-200 ${
          checked ? "bg-tone-accent" : "bg-black/15 dark:bg-white/20"
        }`}
      >
        <span
          className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-md transition-transform duration-200 ${
            checked ? "translate-x-5" : "translate-x-0.5"
          }`}
        />
      </span>
    </button>
  );
}

/** 属性/状态胶囊标签 */
export function RiskTag({
  tone,
  children,
}: {
  tone: "good" | "warn" | "danger" | "accent";
  children: ReactNode;
}) {
  const cls =
    tone === "good"
      ? "bg-tone-good/10 text-tone-good border-tone-good/25"
      : tone === "warn"
        ? "bg-tone-warn/10 text-tone-warn border-tone-warn/25"
        : tone === "danger"
          ? "bg-tone-danger/10 text-tone-danger border-tone-danger/25"
          : "bg-tone-accentSoft text-tone-accent border-tone-accent/25";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${cls}`}>
      {children}
    </span>
  );
}
